import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeftEndOnRectangleIcon,
  BookOpenIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  Bars3Icon,
  WalletIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import useAuth from "../../hooks/useAuth";
import useUser from "../../hooks/useUser";
import logo from "../../assets/logo-light.png";
import userIcon from "../../assets/default_icon.svg";
import "./navbar.css";

const NAVIGATION_ITEMS = [
  { path: "/dashboard", label: "Dashboard", icon: WalletIcon },
  { path: "/sinais-abertos", label: "Sinais", icon: BookOpenIcon },
  { path: "/ranking", label: "Ranking", icon: ChartBarIcon },
  { path: "/configuracoes", label: "Configurações", icon: Cog6ToothIcon },
];

const STORE = {
  url: "https://loja.orionsinais.com.br/",
  label: "Nossa loja",
  icon: BuildingStorefrontIcon,
};

const SUBSCRIPTION_PATHS = ["/assinatura", "/assinatura-terminada"];

const NavMenuItem = ({
  label,
  icon: Icon,
  isActive,
  onClick,
  className = "",
}) => (
  <li
    className={`orion__menu-item ${isActive ? "active" : ""} ${className}`}
    onClick={onClick}
  >
    <Icon className="orion__menu-icon" />
    <p>{label}</p>
  </li>
);

const UserInfo = ({ user, isMobile, onLogout }) => (
  <div className={`orion__userInfo${isMobile ? "-mobile" : ""}`}>
    <img alt="User icon" className="orion__userInfo-img" src={userIcon} />
    <div className="orion__userInfo-text">
      <div className="orion__userInfo-username">
        {user?.first_name ? `${user.first_name} ${user.last_name}` : ""}
      </div>
      {isMobile ? (
        <button
          className="orion__logout orion__desktop-logout-text"
          onClick={onLogout}
        >
          <ArrowLeftEndOnRectangleIcon className="orion__menu-icon-mobile" />
        </button>
      ) : (
        <button className="orion__desktop-logout-text" onClick={onLogout}>
          Sair
        </button>
      )}
    </div>
  </div>
);

const MobileMenu = ({
  isOpen,
  onClose,
  currentPath,
  onNavigate,
  onLogout,
  user,
}) => {
  if (!isOpen) return null;

  return (
    <div className="orion__menu-sidebar">
      <ul>
        <li>
          <XMarkIcon className="orion__menu-icon-mobile" onClick={onClose} />
        </li>
        {NAVIGATION_ITEMS.map(({ path, label, icon }) => (
          <NavMenuItem
            key={path}
            label={label}
            icon={icon}
            isActive={currentPath === path}
            onClick={() => onNavigate(path)}
          />
        ))}
        <li className="orion__menu-item">
          <a href="/blog">
            <BookOpenIcon className="orion__menu-icon" />
            Blog
          </a>
        </li>
        <li className="orion__menu-item">
          <a href={STORE.url} target="_blank" rel="noopener noreferrer">
            <STORE.icon className="orion__menu-icon" />
            {STORE.label}
          </a>
        </li>
        <li>
          <UserInfo user={user} isMobile onLogout={onLogout} />
        </li>
      </ul>
    </div>
  );
};

function Navbar() {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loadingUser } = useUser();
  const { logout, isAuthenticated } = useAuth();

  const handleLogout = () => {
    logout();
    if (!isAuthenticated()) navigate("/login");
  };

  const handleNavigate = (path) => {
    navigate(path);
    setShowMobileMenu(false);
  };

  const isSubscriptionPage = SUBSCRIPTION_PATHS.includes(location.pathname);

  return (
    <div className="orion__navbar">
      <div className="orion__navbar-links">
        <div className="orion__navbar-links_logo" onClick={() => navigate("/")}>
          <img src={logo} alt="logo" className="orion__nav-logo" />
        </div>
      </div>

      {!isSubscriptionPage && (
        <ul className="orion__navbar-menu">
          {NAVIGATION_ITEMS.map(({ path, label, icon }) => (
            <NavMenuItem
              key={path}
              label={label}
              icon={icon}
              isActive={location.pathname === path}
              onClick={() => handleNavigate(path)}
            />
          ))}
        </ul>
      )}

      <Bars3Icon
        className="orion__menu-icon-mobile orion__menu-toggle"
        onClick={() => setShowMobileMenu(true)}
      />

      {!showMobileMenu && (
        <div className="orion__profile-container">
          <a className="orion__blog-link" href="/blog">
            Blog
          </a>
          <a
            className="orion__default-button"
            href={STORE.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {STORE.label}
            <STORE.icon className="orion__menu-icon" />
          </a>
          <UserInfo user={loadingUser ? null : user} onLogout={handleLogout} />
        </div>
      )}

      <MobileMenu
        isOpen={showMobileMenu}
        onClose={() => setShowMobileMenu(false)}
        currentPath={location.pathname}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        user={loadingUser ? null : user}
      />
    </div>
  );
}

export default Navbar;
